import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Coordination, CoordinationClient } from '@atlassiansox/engagekit-ts';
import RequestTypeImage from 'assets/images/request-type-image.svg';
import ErrorBoundary from 'view/error-boundary/error-boundary';
import { OnboardingConsumer } from 'view/onboarding/onboarding';
import * as bp from 'view/styles/breakpoints';
import * as grid from 'view/styles/grid';
import * as zIndex from 'view/styles/z-index';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import OpenIcon from '@atlaskit/icon/glyph/open';
import { SpotlightCard } from '@atlaskit/onboarding';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { PortalAnalyticsInlineDialog } from '@atlassian/help-center-common-util/analytics/screens-without-browsermetrics';
import messages from './messages';

export interface RequestTypeSpotlightProps {
    isAdmin: boolean;
    onEdit: () => void;
    onDismiss?: () => void;
    cloudId: string;
}

const MESSAGE_ID = 'jsd-portal-request-type-onboarding';
const REQUEST_TYPES_ONBOARDING = 'jsd-portal-request-type-onboarding';

export class RequestTypeSpotlight extends React.Component<
    RequestTypeSpotlightProps & InjectedIntlProps & WithAnalyticsEventsProps
> {
    state = {
        show: true,
    };

    // eslint-disable-next-line @typescript-eslint/consistent-type-imports
    buildActions(client: CoordinationClient): import('@atlaskit/onboarding/types').Actions {
        const { intl, onDismiss, onEdit } = this.props;
        const hideSpotlightAndStopMessage = (callback: () => void) => {
            this.setState({ show: false });
            callback(); // call this in handler context so navigation doesn't get blocked by popup blockers.
            // Disabling existing violations, should be fixed when revisited.
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            client.stop(MESSAGE_ID);
        };

        return [
            {
                text: <DismissWrapper>{intl.formatMessage(messages.dismissAction)}</DismissWrapper>,
                appearance: 'subtle',
                onClick: () => {
                    hideSpotlightAndStopMessage(() => {
                        onDismiss && onDismiss();
                    });
                },
            },
            {
                text: (
                    <EditButtonWrapper>
                        {intl.formatMessage(messages.editAction)}
                        <OpenIcon label={intl.formatMessage(messages.editAction)} size={'small'} />
                    </EditButtonWrapper>
                ),
                onClick: () => {
                    hideSpotlightAndStopMessage(() => {
                        onEdit && onEdit();
                    });
                },
            },
        ];
    }

    renderSpotlight() {
        const { intl, cloudId } = this.props;
        if (!this.state.show) {
            return null;
        }

        const client = new CoordinationClient(cloudId, '/gateway/api');

        return (
            <Coordination client={client} messageId={MESSAGE_ID}>
                <FixedSpotlightContainer role="alert">
                    <StyledSpotlightCard
                        image={RequestTypeImage}
                        width={SPOTLIGHT_CARD_WIDTH}
                        actions={this.buildActions(client)}
                        heading={intl.formatMessage(messages.requestTypeSpotlightHeading)}
                    >
                        <FireScreenEventOnMount />
                        <FormattedMessage
                            {...messages.requestTypeSpotlightBody}
                            values={{
                                editRequestType: <strong>{intl.formatMessage(messages.editRequestType)}</strong>,
                            }}
                        />
                    </StyledSpotlightCard>
                </FixedSpotlightContainer>
            </Coordination>
        );
    }

    render() {
        const { isAdmin } = this.props;

        return isAdmin ? (
            <ErrorBoundary id={REQUEST_TYPES_ONBOARDING}>
                <OnboardingConsumer>
                    {(onboarding) =>
                        !onboarding.isDemoProject && !onboarding.isLoading ? this.renderSpotlight() : null
                    }
                </OnboardingConsumer>
            </ErrorBoundary>
        ) : null;
    }
}

export default PortalAnalyticsInlineDialog<RequestTypeSpotlightProps & WithAnalyticsEventsProps>(
    'RequestTypeOnboardingSpotlightCard'
)(injectIntl(RequestTypeSpotlight));

export const SPOTLIGHT_CARD_WIDTH = grid.multiple(40.75).unitless;

const FixedSpotlightContainer = styled.div`
    display: none;
    ${bp.fromXSmall.css`
        display:block;
        position: fixed;
        bottom: ${token('space.200', '16px')};
        width: ${SPOTLIGHT_CARD_WIDTH}px;
        right: ${token('space.200', '16px')};
        z-index: ${zIndex.modal};
    `};
    ${bp.fromXLarge.css`
        position: absolute;
        width: ${SPOTLIGHT_CARD_WIDTH}px;
        top: -${grid.multiple(3).px};
        right: -${SPOTLIGHT_CARD_WIDTH + grid.multiple(5.5).unitless}px;
    `};
`;

const EditButtonWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

// make sure dismiss button is using white color
const DismissWrapper = styled.span`
    color: ${token('color.text.inverse', colors.N0)};
`;

// eslint-disable-next-line rulesdir/no-styled-export
export const StyledSpotlightCard = styled((props: Omit<ExtractProps<typeof SpotlightCard>, 'theme'>) => (
    <SpotlightCard {...props} />
))`
    border-top-left-radius: 0;
    border-top-right-radius: 0;
`;
